import { useState, useEffect, useCallback } from 'react'
import { api } from '../api/client'

export interface AIPromptInputValues {
  prompt: string
  tier: 'high' | 'medium' | 'low'
  reasoning: 'none' | 'low' | 'medium' | 'high'
  quality?: 'standard' | 'hd'
  count?: number
}

interface EffortPref {
  effortLevel: number
  modelID: number
  modelName: string
  displayName: string
  providerID: number
  providerName: string
}

interface ModelRow {
  modelID: number
  modelName: string
  displayName: string
  pricePer1MInput?: number
  pricePer1MOutput?: number
  pricingUnit: string
  isDeployed?: boolean
  deploymentName?: string
  reasoningCapabilities?: string | null
}

export type AIInputMode = 'text' | 'image' | 'transcription' | 'vision' | 'video' | 'audio'

const MODE_TO_FACTORY: Record<AIInputMode, number> = {
  text: 1,
  image: 3,
  video: 4,
  audio: 5,
  transcription: 6,
  vision: 7,
}

interface AIPromptInputProps {
  mode: AIInputMode
  prompt: string
  onPromptChange: (prompt: string) => void
  onGenerate: (values: AIPromptInputValues) => void
  generating?: boolean
  disabled?: boolean
  promptPlaceholder?: string
  promptRows?: number
  generateLabel?: string
  hidePrompt?: boolean
  children?: React.ReactNode
}

const TIER_LABELS: Record<string, string> = {
  high: 'High (frontier)',
  medium: 'Medium (balanced)',
  low: 'Low (fast/cheap)',
}

const TIER_MAP: Record<string, number> = { low: 1, medium: 2, high: 3 }

function formatModelPrice(m: ModelRow): string {
  if (m.pricingUnit === 'image') {
    const inCost = m.pricePer1MInput ?? 0
    const outCost = m.pricePer1MOutput ?? 0
    if (inCost > 0) return `~$${(inCost / 1000).toFixed(2)}/image`
    if (outCost > 0) return `~$${(outCost / 1000).toFixed(2)}/image`
    return 'per image'
  }
  if (m.pricingUnit === 'second') {
    const rate = m.pricePer1MInput ?? m.pricePer1MOutput
    if (rate) return `$${rate}/hr`
    return 'per second'
  }
  if (m.pricingUnit === 'tokens') {
    const inP = m.pricePer1MInput
    const outP = m.pricePer1MOutput
    if (inP != null && outP != null) return `$${inP}/M in, $${outP}/M out`
    if (inP != null) return `$${inP}/M tokens`
    return 'per token'
  }
  return ''
}

export default function AIPromptInput({
  mode,
  prompt,
  onPromptChange,
  onGenerate,
  generating = false,
  disabled = false,
  promptPlaceholder,
  promptRows = 3,
  generateLabel,
  hidePrompt = false,
  children,
}: AIPromptInputProps) {
  const factoryId = MODE_TO_FACTORY[mode]
  const [tier, setTier] = useState<'high' | 'medium' | 'low'>('medium')
  const [reasoning, setReasoning] = useState<'none' | 'low' | 'medium' | 'high'>('low')
  const [quality, setQuality] = useState<'standard' | 'hd'>('standard')
  const [count, setCount] = useState(1)
  const [expanded, setExpanded] = useState(false)

  const [prefs, setPrefs] = useState<EffortPref[]>([])
  const [models, setModels] = useState<ModelRow[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    if (!factoryId) return
    setLoadError(null)
    Promise.all([
      api.get<EffortPref[]>(`/provider-setup/factories/${factoryId}/preferences`),
      api.get<ModelRow[]>(`/provider-setup/factories/${factoryId}/models?includeInactive=false`),
    ]).then(([p, m]) => {
      setPrefs(p)
      setModels(m)
    }).catch(err => {
      console.error('[SMM] Failed to load AI model preferences', err)
      setLoadError('Could not load model preferences')
    })
  }, [factoryId])

  const currentPref = prefs.find(p => p.effortLevel === TIER_MAP[tier])
  const currentModel = currentPref
    ? models.find(m => m.modelID === currentPref.modelID)
    : undefined

  const reasoningLevels = (() => {
    if (mode !== 'text') return []
    if (!currentModel?.reasoningCapabilities) return ['low', 'medium', 'high']
    try {
      const caps = JSON.parse(currentModel.reasoningCapabilities) as string[]
      return caps.length > 0 ? caps : ['low', 'medium', 'high']
    } catch { return ['low', 'medium', 'high'] }
  })()

  useEffect(() => {
    if (reasoningLevels.length > 0 && !reasoningLevels.includes(reasoning)) {
      setReasoning(reasoningLevels[0] as 'low' | 'medium' | 'high')
    }
  }, [reasoningLevels, reasoning])

  const showReasoning = mode === 'text' && reasoningLevels.length > 0
  const showQuality = mode === 'image'
  const showCount = mode === 'image'

  const handleGenerate = useCallback(() => {
    const values: AIPromptInputValues = {
      prompt,
      tier,
      reasoning: showReasoning ? reasoning : 'none',
    }
    if (showQuality) values.quality = quality
    if (showCount) values.count = count
    onGenerate(values)
  }, [prompt, tier, reasoning, quality, count, showReasoning, showQuality, showCount, onGenerate])

  const defaultPlaceholder = mode === 'image'
    ? 'Describe the image you want to generate...'
    : mode === 'transcription'
    ? 'Additional transcription instructions (optional)...'
    : 'Describe what you want the AI to do...'

  const defaultLabel = generating
    ? 'Generating...'
    : mode === 'image'
    ? 'Generate with AI'
    : 'Generate'

  return (
    <div className="smm-ai-prompt-input">
      {!hidePrompt && (
        <div className="smm-ai-prompt-wrap">
          <div className="smm-ai-prompt-header">
            <span className="smm-ai-prompt-label">Editable prompt:</span>
            <button
              type="button"
              className="smm-ai-expand-btn"
              onClick={() => setExpanded(e => !e)}
              title={expanded ? 'Collapse' : 'Expand'}
            >{expanded ? '▲ Collapse' : '▼ Expand'}</button>
          </div>
          <textarea
            className="smm-textarea"
            rows={expanded ? Math.max(promptRows, 8) : promptRows}
            value={prompt}
            onChange={e => onPromptChange(e.target.value)}
            placeholder={promptPlaceholder || defaultPlaceholder}
            disabled={disabled || generating}
            style={expanded ? { minHeight: '180px' } : undefined}
          />
        </div>
      )}

      <div className="smm-ai-controls">
        <label className="smm-ai-control-label">
          Model Tier:
          <select
            className="smm-input smm-input-sm"
            value={tier}
            onChange={e => setTier(e.target.value as 'high' | 'medium' | 'low')}
            disabled={disabled || generating}
          >
            {(['high', 'medium', 'low'] as const).map(t => (
              <option key={t} value={t}>{TIER_LABELS[t]}</option>
            ))}
          </select>
        </label>

        {showReasoning && (
          <label className="smm-ai-control-label">
            Reasoning:
            <select
              className="smm-input smm-input-sm"
              value={reasoning}
              onChange={e => setReasoning(e.target.value as 'none' | 'low' | 'medium' | 'high')}
              disabled={disabled || generating}
            >
              {reasoningLevels.map(level => (
                <option key={level} value={level}>
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </option>
              ))}
            </select>
          </label>
        )}

        {showQuality && (
          <label className="smm-ai-control-label">
            Quality:
            <select
              className="smm-input smm-input-sm"
              value={quality}
              onChange={e => setQuality(e.target.value as 'standard' | 'hd')}
              disabled={disabled || generating}
            >
              <option value="standard">Standard</option>
              <option value="hd">HD</option>
            </select>
          </label>
        )}

        {showCount && (
          <label className="smm-ai-control-label">
            Variations:
            <select
              className="smm-input smm-input-sm"
              value={count}
              onChange={e => setCount(Number(e.target.value))}
              disabled={disabled || generating}
            >
              {[1, 2, 3, 4, 5].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        )}

        {children}

        <button
          className="smm-btn smm-btn-primary smm-btn-sm"
          onClick={handleGenerate}
          disabled={disabled || generating || (!hidePrompt && !prompt.trim())}
        >
          {generateLabel || defaultLabel}
        </button>
      </div>

      {/* Model info bar: show selected model name + price below controls */}
      {currentPref && (
        <div className="smm-ai-model-info">
          <span className="smm-ai-model-name">
            {currentPref.displayName}
          </span>
          <span className="smm-ai-model-provider">
            {currentPref.providerName}
          </span>
          {currentModel && (
            <span className="smm-ai-model-price">
              {formatModelPrice(currentModel)}
            </span>
          )}
          {currentModel?.isDeployed === false && (
            <span className="smm-ai-model-warning">Not deployed</span>
          )}
        </div>
      )}

      {!currentPref && prefs.length > 0 && (
        <div className="smm-ai-model-info smm-ai-model-warning">
          No model assigned to {TIER_LABELS[tier]} tier. Set a default in Admin &rarr; AI Providers.
        </div>
      )}

      {loadError && (
        <div className="smm-ai-model-info smm-ai-model-warning">{loadError}</div>
      )}
    </div>
  )
}
